import { useTranslation } from 'react-i18next';
import { IdentityAvatarGrid } from '@/components';
import type { UndefinedIdentityCellConfig } from '@/components/IdentityAvatarGrid';
import type { ActionMenuItem } from '@/components/EntityActionsMenu/EntityActionsMenu';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Identity, IdentityToken } from '@/types/identity';

const SIDEBAR_GRID_COLUMNS = 3;
const SIDEBAR_PAGINATION = { initialRows: 4, pageSize: 12 };
const SIDEBAR_SCROLL_AREA = {
    maxRows: 7,
    availableHeightClassName: 'max-h-[max(76px,calc(100dvh-424px))]',
    fadeClassName:
        'h-[22px] bg-[linear-gradient(180deg,rgba(17,18,20,0),var(--grey-700)_83%)]',
};

interface ReportSidebarIdentitiesProps {
    identities: Identity[];
    selectedTokens: Set<IdentityToken>;
    onToggle: (token: IdentityToken) => void;
    onClear: () => void;
    undefinedCells: UndefinedIdentityCellConfig[];
    buildMenuItems: (identity: Identity) => ActionMenuItem[];
}

export function ReportSidebarIdentities({
    identities,
    selectedTokens,
    onToggle,
    onClear,
    undefinedCells,
    buildMenuItems,
}: ReportSidebarIdentitiesProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);

    if (identities.length === 0 && undefinedCells.length === 0) {
        return null;
    }

    return (
        <div className="flex min-h-0 flex-col gap-4">
            <div className="flex items-center justify-between">
                <span className="text-sm-medium text-white-100">
                    {t('identityFilter.title')}
                </span>
                {selectedTokens.size > 0 && (
                    <button
                        type="button"
                        onClick={onClear}
                        data-testid="report-sidebar-identities-clear"
                        className="text-sm-medium text-grey-300 transition-colors hover:text-white-100"
                    >
                        {t('clear')}
                    </button>
                )}
            </div>
            <IdentityAvatarGrid
                identities={identities}
                selectedTokens={selectedTokens}
                onToggle={onToggle}
                buildMenuItems={buildMenuItems}
                columns={SIDEBAR_GRID_COLUMNS}
                showLabels={false}
                size="md"
                undefinedCells={undefinedCells}
                pagination={SIDEBAR_PAGINATION}
                scrollArea={SIDEBAR_SCROLL_AREA}
                emptyLabel={t('identityFilter.empty')}
            />
        </div>
    );
}
